export type Language = 'zh' | 'en';

type TranslationItem = {
  [key: string]: string | TranslationItem | Array<TranslationItem>;
};

type TranslationType = {
  [key in Language]: {
    navigation: {
      home: string;
      features: string;
      faq: string;
      pricing: string;
      welcome: string;
      mySubscription: string;
      signOut: string;
      signInWithGoogle: string;
    };
    hero: {
      title: string;
      description: string;
      primaryButton: string;
      secondaryButton: string;
    };
    features: {
      title: string;
      items: Array<{
        title: string;
        description: string;
      }>;
    };
    pricing: {
      title: string;
      subtitle: string;
      monthly: string;
      yearly: string;
      save: string;
      mo: string;
      yr: string;
      free: string;
      mostPopular: string;
      processing: string;
      getStarted: string;
      subscribe: string;
      description: string;
      plans: Array<{
        name: string;
        price: string;
        features: string[];
        buttonText: string;
      }>;
    };
    faq: {
      title: string;
      items: Array<{
        question: string;
        answer: string;
      }>;
    };
    plans: {
      [key: string]: {
        name: string;
        description: string;
        features: string[];
      };
    };
    loading: string;
    error: string;
    mySubscription: string;
    pleaseSignIn: string;
    signInToViewSubscription: string;
    signIn: string;
    noActiveSubscription: string;
    subscribeToUnlockFeatures: string;
    viewPlans: string;
    accountInformation: string;
    name: string;
    email: string;
    unknown: string;
    month: string;
    year: string;
    currentPeriod: string;
    willCancelAtPeriodEnd: string;
    willAutoRenew: string;
    subscriptionStartedOn: string;
    manageSubscription: string;
    changePlan: string;
    redirecting: string;
    portalOpenFailed: string;
    noCustomerId: string;
    serverError: string;
    noPortalUrl: string;
    lifetimeAccess: string;
    lifetimeSubscription: string;
    purchaseDate: string;
    oneTimePayment: string;
    viewOtherPlans: string;
    login: {
      title: string;
      description: string;
      button: string;
    };
  };
};

export const translations: TranslationType = {
  zh: {
    navigation: {
      home: '首页',
      features: '功能',
      faq: '常见问题',
      pricing: '价格',
      welcome: '欢迎',
      mySubscription: '我的订阅',
      signOut: '退出登录',
      signInWithGoogle: '通过Google登录'
    },
    hero: {
      title: '提升您的生产力\n使用我们的扩展',
      description: '使用我们设计的强大工具转变您的浏览体验，让工作更智能，而不是更困难。',
      primaryButton: '添加到 Chrome - 免费试用',
      secondaryButton: '了解更多'
    },
    features: {
      title: '强大功能',
      items: [
        {
          title: '闪电般快速',
          description: '即时访问强大的功能，零延迟响应，让您的工作效率倍增。'
        },
        {
          title: '节省时间',
          description: '智能自动化重复性任务，让您专注于真正重要的事项。'
        },
        {
          title: '安全可靠',
          description: '采用先进的加密技术，确保您的数据始终保持私密和安全。'
        },
        {
          title: '智能功能',
          description: '根据您的使用习惯不断学习和适应，提供个性化的工作体验。'
        }
      ]
    },
    pricing: {
      title: '简单透明的价格',
      subtitle: '选择最适合您的方案',
      monthly: '月付',
      yearly: '年付',
      save: '节省17%',
      mo: '月',
      yr: '年',
      free: '免费',
      mostPopular: '最受欢迎',
      processing: '处理中...',
      getStarted: '开始使用',
      subscribe: '立即订阅',
      description: '选择最适合您的方案',
      plans: [
        {
          name: '一个月免费试用',
          price: '免费',
          features: [
            'Markdown 格式预览和渲染',
            'LaTeX 数学公式支持',
            'JSON 数据格式化显示',
            '链接预览和富文本渲染',
            '代码语法高亮',
            '表格数据格式化',
            '图片链接预览'
          ],
          buttonText: '开始免费试用'
        },
        {
          name: '专业版',
          price: '$9.9/年',
          features: [
            'Markdown 格式预览和渲染',
            'LaTeX 数学公式支持',
            'JSON 数据格式化显示',
            '链接预览和富文本渲染',
            '代码语法高亮',
            '表格数据格式化',
            '图片链接预览'
          ],
          buttonText: '立即订阅'
        },
        {
          name: '终身版',
          price: '$19.9/终身',
          features: [
            'Markdown 格式预览和渲染',
            'LaTeX 数学公式支持',
            'JSON 数据格式化显示',
            '链接预览和富文本渲染',
            '代码语法高亮',
            '表格数据格式化',
            '图片链接预览'
          ],
          buttonText: '立即购买'
        }
      ]
    },
    faq: {
      title: '常见问题',
      items: [
        {
          question: '支持哪些电子表格平台？',
          answer: '目前，我们支持Google Sheets和飞书表格。我们正在努力添加对更多平台的支持。'
        },
        {
          question: '支持哪些内容格式？',
          answer: '我们支持Markdown、公式、JSON、链接和纯文本。预览功能会自动检测内容类型并适当渲染。'
        },
        {
          question: '单元格比较功能如何使用？',
          answer: '只需选择两个单元格，我们的扩展就会显示它们内容的并排比较，突出显示差异和相似之处。'
        },
        {
          question: '我的数据安全吗？',
          answer: '是的，我们非常重视安全性。所有数据处理都在您的浏览器本地进行，我们从不存储或传输您的电子表格数据。'
        },
        {
          question: '为什么某些链接无法预览？',
          answer: '某些网站出于安全原因禁止在iframe中嵌入其内容。这是由这些网站的安全策略决定的，我们无法绕过这些限制。在这种情况下，您可以点击链接在新标签页中打开。'
        }
      ]
    },
    plans: {
      'pro-yearly': {
        name: '专业版',
        description: '适合个人用户和小型团队',
        features: [
          '包含所有免费版功能',
          '高级单元格对比',
          '自定义格式选项',
          '优先支持',
          '新功能优先体验',
          '无限表格',
          '扩展格式选项'
        ],
      },
      'pro-lifetime': {
        name: '终身版',
        description: '一次性付费，终身使用',
        features: [
          '包含所有专业版功能',
          '高级单元格对比',
          '自定义格式选项',
          '优先支持',
          '新功能优先体验',
          '无限表格',
          '扩展格式选项'
        ],
      },
    },
    loading: '加载中...',
    error: '出错了',
    mySubscription: '我的订阅',
    pleaseSignIn: '请先登录',
    signInToViewSubscription: '请登录后查看您的订阅信息',
    signIn: '登录',
    noActiveSubscription: '您还没有活跃的订阅',
    subscribeToUnlockFeatures: '订阅我们的服务以解锁全部功能。',
    viewPlans: '查看订阅计划',
    accountInformation: '账户信息',
    name: '用户名',
    email: '邮箱',
    unknown: '未知',
    month: '月',
    year: '年',
    currentPeriod: '当前订阅周期',
    willCancelAtPeriodEnd: '将在当前周期结束后取消',
    willAutoRenew: '将在当前周期结束后自动续订',
    subscriptionStartedOn: '订阅开始于',
    manageSubscription: '管理订阅',
    changePlan: '更改计划',
    redirecting: '正在跳转...',
    portalOpenFailed: '无法打开订阅管理门户，请稍后再试。',
    noCustomerId: '未找到客户ID，无法管理订阅。',
    serverError: '服务器错误',
    noPortalUrl: '未获取到门户链接',
    lifetimeAccess: '终身访问权（永久有效）',
    lifetimeSubscription: '终身订阅',
    purchaseDate: '购买日期',
    oneTimePayment: '一次性付款',
    viewOtherPlans: '查看其他计划',
    login: {
      title: '登录以继续',
      description: '请使用您的Google账号登录以继续订阅流程。我们不会在未经您许可的情况下发布任何内容。',
      button: '使用Google登录'
    },
  },
  en: {
    navigation: {
      home: 'Home',
      features: 'Features',
      faq: 'FAQ',
      pricing: 'Pricing',
      welcome: 'Welcome',
      mySubscription: 'My Subscription',
      signOut: 'Sign Out',
      signInWithGoogle: 'Sign in with Google'
    },
    hero: {
      title: 'Enhance Your Spreadsheet\nwith Smart Cell Preview',
      description: 'Instantly preview and compare cell contents in Google Sheets and Lark Suite with rich formatting support for Markdown, formulas, JSON, and more.',
      primaryButton: 'Add to Chrome - Free Trial',
      secondaryButton: 'Watch Demo'
    },
    features: {
      title: 'Powerful Features',
      items: [
        {
          title: 'Smart Cell Preview',
          description: 'Real-time preview of cell contents with support for Markdown, formulas, JSON, and links in a beautiful sidebar.'
        },
        {
          title: 'Cell Comparison',
          description: 'Easily compare contents between two cells to spot differences and analyze data more effectively.'
        },
        {
          title: 'Rich Formatting',
          description: 'Customize font styles, sizes, and colors to make your data more readable and visually appealing.'
        },
        {
          title: 'Auto-Sync',
          description: 'Automatically updates preview when switching between cells, ensuring you always see the latest content.'
        }
      ]
    },
    pricing: {
      title: 'Simple, Transparent Pricing',
      subtitle: 'Choose the plan that best fits your needs',
      monthly: 'Monthly',
      yearly: 'Yearly',
      save: 'Save 17%',
      mo: 'mo',
      yr: 'yr',
      free: 'Free',
      mostPopular: 'Most Popular',
      processing: 'Processing...',
      getStarted: 'Get Started',
      subscribe: 'Subscribe Now',
      description: 'Choose the plan that best fits your needs',
      plans: [
        {
          name: '1-Month Free Trial',
          price: 'Free',
          features: [
            'Markdown preview and rendering',
            'LaTeX math formula support',
            'JSON data formatting',
            'Link preview and rich text',
            'Code syntax highlighting',
            'Table data formatting',
            'Image link preview'
          ],
          buttonText: 'Start Free Trial'
        },
        {
          name: 'Pro',
          price: '$9.9/year',
          features: [
            'Markdown preview and rendering',
            'LaTeX math formula support',
            'JSON data formatting',
            'Link preview and rich text',
            'Code syntax highlighting',
            'Table data formatting',
            'Image link preview'
          ],
          buttonText: 'Subscribe Now'
        },
        {
          name: 'Lifetime',
          price: '$19.9/lifetime',
          features: [
            'Markdown preview and rendering',
            'LaTeX math formula support',
            'JSON data formatting',
            'Link preview and rich text',
            'Code syntax highlighting',
            'Table data formatting',
            'Image link preview'
          ],
          buttonText: 'Buy Now'
        }
      ]
    },
    faq: {
      title: 'Frequently Asked Questions',
      items: [
        {
          question: 'Which spreadsheet platforms are supported?',
          answer: 'Currently, we support Google Sheets and Lark Suite. We\'re working on adding support for more platforms in the future.'
        },
        {
          question: 'What content formats are supported?',
          answer: 'We support Markdown, formulas, JSON, links, and plain text. The preview automatically detects the content type and renders it appropriately.'
        },
        {
          question: 'How does the cell comparison feature work?',
          answer: 'Simply select two cells, and our extension will show a side-by-side comparison of their contents, highlighting differences and similarities.'
        },
        {
          question: 'Is my data secure?',
          answer: 'Yes, we take security seriously. All data processing happens locally in your browser, and we never store or transmit your spreadsheet data.'
        },
        {
          question: 'Why can\'t some links be previewed?',
          answer: 'Some websites prohibit embedding their content in iframes for security reasons. This is determined by the security policies of these sites, and we cannot bypass these restrictions. In such cases, you can click the link to open it in a new tab.'
        }
      ]
    },
    plans: {
      'pro-yearly': {
        name: 'Pro',
        description: 'For individuals and small teams',
        features: [
          'All Free features',
          'Advanced cell comparison',
          'Custom formatting options',
          'Priority support',
          'Early access to new features',
          'Unlimited sheets',
          'Extended formatting options'
        ],
      },
      'pro-lifetime': {
        name: 'Lifetime',
        description: 'One-time payment, lifetime access',
        features: [
          'All Pro features',
          'Advanced cell comparison',
          'Custom formatting options',
          'Priority support',
          'Early access to new features',
          'Unlimited sheets',
          'Extended formatting options'
        ],
      },
    },
    loading: 'Loading...',
    error: 'Error',
    mySubscription: 'My Subscription',
    pleaseSignIn: 'Please Sign In',
    signInToViewSubscription: 'Please sign in to view your subscription information',
    signIn: 'Sign In',
    noActiveSubscription: 'No Active Subscription',
    subscribeToUnlockFeatures: 'Subscribe to our service to unlock all features.',
    viewPlans: 'View Plans',
    accountInformation: 'Account Information',
    name: 'Name',
    email: 'Email',
    unknown: 'Unknown',
    month: 'month',
    year: 'year',
    currentPeriod: 'Current Period',
    willCancelAtPeriodEnd: 'Will cancel at the end of the current period',
    willAutoRenew: 'Will automatically renew after the current period',
    subscriptionStartedOn: 'Subscription started on',
    manageSubscription: 'Manage Subscription',
    changePlan: 'Change Plan',
    redirecting: 'Redirecting...',
    portalOpenFailed: 'Could not open subscription management portal, please try again later.',
    noCustomerId: 'No customer ID found, cannot manage subscription.',
    serverError: 'Server Error',
    noPortalUrl: 'No portal URL received',
    lifetimeAccess: 'Lifetime Access (Never Expires)',
    lifetimeSubscription: 'Lifetime Subscription',
    purchaseDate: 'Purchase Date',
    oneTimePayment: 'One-time Payment',
    viewOtherPlans: 'View Other Plans',
    login: {
      title: 'Sign in to continue',
      description: 'Please sign in with your Google account to continue the subscription process. We won\'t post anything without your permission.',
      button: 'Sign in with Google'
    },
  }
}; 